import { useState } from 'react'
import { Flex, IconButton, Text } from '@radix-ui/themes'
import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons'
import { formatValue } from './claim-form-utils'
import { FieldRow } from './claim-form-fields'
import { ExtraTicketFieldsReadOnly } from './ticket-extra-fields'

type TicketFieldsProps = {
  tickets: unknown[]
  paymentMethod: unknown
  schema?: Record<string, unknown>
  currentIndex?: number
  onIndexChange?: (index: number) => void
}

export function TicketFields({
  tickets,
  paymentMethod,
  schema,
  currentIndex: controlledIndex,
  onIndexChange,
}: TicketFieldsProps) {
  const [internalIndex, setInternalIndex] = useState(controlledIndex ?? 0)
  const currentIndex = controlledIndex ?? internalIndex
  const [jumpInput, setJumpInput] = useState(String(currentIndex + 1))
  const total = tickets.length
  const ticket = tickets[currentIndex] as Record<string, unknown>

  const goTo = (index: number) => {
    const clamped = Math.max(0, Math.min(total - 1, index))
    if (onIndexChange) {
      onIndexChange(clamped)
    } else {
      setInternalIndex(clamped)
    }
    setJumpInput(String(clamped + 1))
  }

  return (
    <Flex direction="column" gap="3">
      <div className="claim-form-ticket-summary">
        <FieldRow label="Number of Tickets" value={String(total)} />
        <FieldRow label="Payment Method" value={formatValue(paymentMethod)} />
      </div>

      <Text size="2" weight="bold">
        Ticket {currentIndex + 1} of {total}
      </Text>

      <div className="claim-form-three-col">
        <FieldRow
          label="Prize Amount"
          value={
            ticket?.prizeAmount !== undefined &&
            ticket?.prizeAmount !== null &&
            ticket?.prizeAmount !== ''
              ? `$${Number(ticket.prizeAmount).toLocaleString()}`
              : null
          }
        />
        <FieldRow label="Game Type" value={formatValue(ticket?.gameType)} />
        <FieldRow label="Barcode Number" value={formatValue(ticket?.barcodeNumber)} />
        <ExtraTicketFieldsReadOnly ticket={ticket ?? {}} schema={schema} />
      </div>

      {total > 1 ? (
        <Flex direction="column" align="center" gap="1" className="claim-form-ticket-nav">
          <Text size="1" color="gray">
            Jump to Ticket
          </Text>
          <Flex align="center" gap="2">
            <IconButton
              size="1"
              variant="soft"
              color="gray"
              disabled={currentIndex === 0}
              onClick={() => goTo(currentIndex - 1)}>
              <ChevronLeftIcon />
            </IconButton>
            <input
              className="claim-form-ticket-jump-input"
              value={jumpInput}
              onChange={(e) => setJumpInput(e.target.value)}
              onBlur={() => {
                const num = parseInt(jumpInput, 10)
                if (!isNaN(num)) {
                  goTo(num - 1)
                } else {
                  setJumpInput(String(currentIndex + 1))
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.currentTarget.blur()
                }
              }}
            />
            <Text size="1" color="gray">
              of {total}
            </Text>
            <IconButton
              size="1"
              variant="soft"
              color="gray"
              disabled={currentIndex === total - 1}
              onClick={() => goTo(currentIndex + 1)}>
              <ChevronRightIcon />
            </IconButton>
          </Flex>
        </Flex>
      ) : null}
    </Flex>
  )
}
